import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from './Modal';
import { searchService } from '../services/searchService';
import EnhancedImage from './EnhancedImage';
import { SearchIcon, CloseIcon } from './Icons';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose, initialQuery = '' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [page, setPage] = useState(1);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 768;

  // 当模态框打开时，聚焦到搜索框
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // 处理初始查询参数
  useEffect(() => {
    if (isOpen && initialQuery && initialQuery !== query) {
      setQuery(initialQuery);
      
      // 如果是标签搜索，切换到片语标签页
      if (initialQuery.startsWith('tag:')) {
        setActiveTab('idea');
      }
      
      // 自动执行搜索
      setTimeout(() => {
        handleSearchWithQuery(initialQuery);
      }, 100);
    }
  }, [isOpen, initialQuery]);

  // 处理搜索（使用指定查询）
  const handleSearchWithQuery = useCallback(async (searchQuery = query) => {
    if (!searchQuery.trim()) {
      setError('请输入搜索关键词');
      setResults(null);
      return;
    }

    setLoading(true);
    setError(null);
    setShowSuggestions(false);

    try {
      const searchResults = await searchService.search(searchQuery, {
        page,
        pageSize: 12
      });
      setResults(searchResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('搜索出错，请稍后再试');
    } finally {
      setLoading(false);
    }
  }, [query, page]);

  // 处理搜索
  const handleSearch = useCallback(async () => {
    return handleSearchWithQuery(query);
  }, [handleSearchWithQuery, query]);

  // 处理输入变化
  const handleInputChange = async (e) => {
    const value = e.target.value;
    setQuery(value);

    // 获取搜索建议
    if (value.length >= 2) {
      try {
        const newSuggestions = await searchService.getSuggestions(value);
        setSuggestions(newSuggestions);
        setShowSuggestions(newSuggestions.length > 0);
      } catch (err) {
        console.error('Error getting suggestions:', err);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // 处理键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // 防止表单提交
      setShowSuggestions(false);
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // 处理点击建议
  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    handleSearch();
  };

  // 处理点击外部关闭建议
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      // 更新isMobile状态 - 在实际应用中可以设置状态
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 处理切换标签
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  // 处理页码变化
  const handlePageChange = (newPage) => {
    setPage(newPage);
    handleSearch();
  };

  // 处理结果点击，关闭模态框并导航
  const handleResultClick = (item) => {
    onClose();
    
    // 根据内容类型导航到不同页面
    switch (item.type) {
      case 'article':
        navigate(`/article/${item.slug}`);
        break;
      case 'weekly':
        navigate(`/weekly/${item.slug}`);
        break;
      case 'idea':
        navigate(`/ideas?highlight=${item.documentId || item.id}`);
        break;
      case 'stash':
        navigate(`/stash/${item.stashType?.slug}/${item.slug}`);
        break;
      default:
        break;
    }
  };

  // 处理标签点击，阻止事件冒泡并进行标签搜索
  const handleTagClick = (e, tagName) => {
    e.stopPropagation(); // 阻止事件冒泡到父元素
    e.preventDefault();
    
    // 设置搜索查询为标签名
    const tagQuery = `tag:${tagName}`;
    setQuery(tagQuery);
    setShowSuggestions(false);
    setActiveTab('idea'); // 切换到片语标签页
    
    // 执行搜索
    handleSearchWithQuery(tagQuery).catch(err => {
      console.error('Tag search error:', err);
      setError('标签搜索出错，请稍后再试');
    });
  };

  // 渲染搜索结果
  const renderResults = () => {
    if (!results) return null;
    
    // 根据当前标签筛选结果
    let displayResults = [];
    
    if (activeTab === 'all') {
      displayResults = results.results;
    } else {
      displayResults = results.byType[`${activeTab}s`] || [];
    }

    if (displayResults.length === 0) {
      return (
        <div className="search-no-results">
          <p>未找到与 "{query}" 相关的{getTabName(activeTab)}内容</p>
        </div>
      );
    }

    return (
      <>
        <div className="search-results-list">
          {displayResults.map((item) => renderResultItem(item))}
        </div>
        
        {/* 分页 */}
        {results.pageCount > 1 && (
          <div className="search-pagination">
            <button
              className="pagination-button"
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
            >
              上一页
            </button>
            <span className="pagination-info">
              {page} / {results.pageCount}
            </span>
            <button
              className="pagination-button"
              disabled={page === results.pageCount}
              onClick={() => handlePageChange(page + 1)}
            >
              下一页
            </button>
          </div>
        )}
      </>
    );
  };

  // 渲染单个结果项
  const renderResultItem = (item) => {
    switch (item.type) {
      case 'article':
      case 'weekly':
        return (
          <div
            key={`${item.type}-${item.id}`}
            className="search-result-item"
            onClick={() => handleResultClick(item)}
          >
            <div className="result-image">
              <EnhancedImage
                src={item.coverUrl}
                alt={item.title}
                width={400}
                height={200}
                fallbackType={item.type}
              />
            </div>
            <div className="result-content">
              <h4 className="result-title">{highlightText(item.title, query)}</h4>
              <p className="result-description">{highlightText(truncateText(item.description, 80), query)}</p>
              <div className="result-meta">
                <span className="result-type">{getTypeName(item.type)}</span>
                <span className="result-date">
                  {new Date(item.publishedAt).toLocaleDateString('zh-CN')}
                </span>
              </div>
            </div>
          </div>
        );
      
      case 'idea':
        return (
          <div
            key={`idea-${item.id}`}
            className="search-result-item"
            onClick={() => handleResultClick(item)}
          >
            {((item.media && item.media.length > 0) || (item.mediaUrls && item.mediaUrls.length > 0)) && (
              <div className="result-image">
                {(item.mediaUrls || item.media).slice(0, 1).map((media, index) => (
                  <EnhancedImage
                    key={index}
                    src={media.url}
                    alt={media.alt || `媒体 ${index + 1}`}
                    width={400}
                    height={200}
                    className="result-media-main"
                    fallbackType="idea"
                    priority={false}
                  />
                ))}
              </div>
            )}
            <div className="result-content">
              <h4 className="result-title">片语</h4>
              <p className="result-description">{highlightText(truncateText(item.content, 100), query)}</p>
              {((item.media && item.media.length > 1) || (item.mediaUrls && item.mediaUrls.length > 1)) && (
                <div className="result-media-count">
                  <span>+{(item.mediaUrls || item.media).length - 1} 张图片</span>
                </div>
              )}
              {/* 显示标签 */}
              {item.tags && item.tags.length > 0 && (
                <div className="result-tags">
                  {item.tags.map(tag => (
                    <span 
                      key={tag.id} 
                      className="result-tag"
                      onClick={(e) => handleTagClick(e, tag.name)}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              )}
              <div className="result-meta">
                <span className="result-type">片语</span>
                <span className="result-date">
                  {new Date(item.datetime).toLocaleDateString('zh-CN')}
                </span>
              </div>
            </div>
          </div>
        );
      
      case 'stash':
        return (
          <div
            key={`stash-${item.id}`}
            className="search-result-item"
            onClick={() => handleResultClick(item)}
          >
            <div className="result-image">
              <EnhancedImage
                src={item.coverUrl}
                alt={item.title}
                width={400}
                height={200}
                fallbackType="stash"
              />
            </div>
            <div className="result-content">
              <h4 className="result-title">{highlightText(item.title, query)}</h4>
              <p className="result-description">{highlightText(truncateText(item.description, 80), query)}</p>
              <div className="result-meta">
                <span className="result-type">
                  {item.stashType?.name || '私藏'}
                </span>
                <span className="result-date">
                  {new Date(item.createdAt).toLocaleDateString('zh-CN')}
                </span>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // 截断文本函数
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // 高亮搜索关键字
  const highlightText = (text, searchQuery) => {
    if (!text || !searchQuery) return text;
    
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|\[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      if (regex.test(part)) {
        return <mark key={index} className="search-highlight">{part}</mark>;
      }
      return part;
    });
  };

  // 获取内容类型名称
  const getTypeName = (type) => {
    switch (type) {
      case 'article': return '文章';
      case 'weekly': return '周刊';
      case 'idea': return '片语';
      case 'stash': return '私藏';
      default: return '内容';
    }
  };

  // 获取标签名称
  const getTabName = (tab) => {
    switch (tab) {
      case 'article': return '文章';
      case 'weekly': return '周刊';
      case 'idea': return '片语';
      case 'stash': return '私藏';
      case 'all': return '';
      default: return '';
    }
  };

  // 处理模态框关闭，清空所有搜索信息
  const handleModalClose = () => {
    setQuery('');
    setResults(null);
    setError(null);
    setShowSuggestions(false);
    setActiveTab('all');
    setPage(1);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      width="100%"
      fullHeight={false}
      hideCloseButton={true}
      preventOutsideClick={false}
      className="search-modal"
    >
      <div className="search-modal-container">
        <div className="search-header">
          <div className="search-section search-icon-section">
            <SearchIcon size={20} className="search-icon" />
          </div>
          
          <div className="search-section search-input-section">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="搜索文章、周刊、片语、私藏..."
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            {query && (
              <button
                className="search-clear-button"
                onClick={() => {
                  setQuery('');
                  setResults(null);
                  setError(null);
                  setShowSuggestions(false);
                }}
                aria-label="清除搜索"
              >
                ×
              </button>
            )}
          </div>
          
          <div className="search-section search-buttons-section">
            <button className="search-button" onClick={handleSearch}>
              搜索
            </button>
            <button className="search-close-button" onClick={handleModalClose}>
              <CloseIcon size={18} />
            </button>
          </div>
        </div>

        {/* 搜索建议 */}
        {showSuggestions && (
          <div className="search-suggestions" ref={suggestionsRef}>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="search-suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <SearchIcon size={14} className="suggestion-icon" />
                <span>{suggestion}</span>
              </div>
            ))}
          </div>
        )}

        {/* 搜索结果 */}
        {loading ? (
          <div className="search-loading">
            <div className="search-spinner"></div>
            <p>正在搜索...</p>
          </div>
        ) : error ? (
          <div className="search-error">
            <p>{error}</p>
            <button onClick={handleSearch} className="search-retry-button">
              重试
            </button>
          </div>
        ) : results ? (
          <div className="search-results">
            <div className="search-tabs">
              <button
                className={`search-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => handleTabChange('all')}
                data-count={results.total || 0}
              >
                全部 ({results.total || 0})
              </button>
              <button
                className={`search-tab ${activeTab === 'article' ? 'active' : ''}`}
                onClick={() => handleTabChange('article')}
                data-count={results.byType.articles.length || 0}
              >
                文章 ({results.byType.articles.length || 0})
              </button>
              <button
                className={`search-tab ${activeTab === 'weekly' ? 'active' : ''}`}
                onClick={() => handleTabChange('weekly')}
                data-count={results.byType.weeklies.length || 0}
              >
                周刊 ({results.byType.weeklies.length || 0})
              </button>
              <button
                className={`search-tab ${activeTab === 'idea' ? 'active' : ''}`}
                onClick={() => handleTabChange('idea')}
                data-count={results.byType.ideas.length || 0}
              >
                片语 ({results.byType.ideas.length || 0})
              </button>
              <button
                className={`search-tab ${activeTab === 'stash' ? 'active' : ''}`}
                onClick={() => handleTabChange('stash')}
                data-count={results.byType.stashes.length || 0}
              >
                私藏 ({results.byType.stashes.length || 0})
              </button>
            </div>
            {renderResults()}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default SearchModal;